@use 'sass:math';
@use 'config' as config;
@use '~@cewe-designsystem-stylecomponents/mixins/breakpoints' as breakpoints;
@use '~@cewe-designsystem-stylecomponents/button/button-old/float' as buttonFloat with (
  $button-float-background-color: config.$accordion-gallery-v2-content-box-toggle-background-color-visible,
  $button-float-icon-color: config.$accordion-gallery-v2-content-box-toggle-text-color-hidden
);

:host {
  position: relative;
  z-index: 10;
  flex-grow: 1;
  overflow: hidden;
  /* outline is used in js to check if content is open */
  outline: 0 solid transparent;

  /* Mobile */
  @include breakpoints.breakpoint('lg', 'down') {
    width: 100%;
    min-height: 2.5rem;
    transition: height config.$animation-duration config.$animation-cubic;
  }

  /* Desktop */
  @include breakpoints.breakpoint('lg', 'up') {
    min-width: 2.5rem;
    height: 100%;
    overflow: hidden;
    transition: width config.$animation-duration config.$animation-cubic;
  }

  /* Label which opens the content tab. Invisible, overlays the whole content. */
  click-target {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  /* Background image for content while no tab is open */
  slot[name='background'] {
    display: block;
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    height: 100%;
    transition: opacity config.$animation-duration config.$animation-cubic;
    opacity: 0;
    pointer-events: none;
  }

  /* Close button. Only visible in expanded content tab. Not on mobile. */
  close {
    --button-flat-raised-y-padding: 10px;
    --button-flat-raised-x-padding: 10px;

    @extend %csc-button-float;

    position: absolute;
    top: 1rem;
    right: 1rem;

    box-sizing: border-box;
    transition: opacity config.$animation-duration config.$animation-cubic;
    opacity: 0;
    background-color: config.$accordion-gallery-v2-close-button-text-background-color;
    pointer-events: none;

    /* No close button on mobile */
    @include breakpoints.breakpoint('lg', 'down') {
      display: none;
    }

    &:hover {
      background-color: config.$accordion-gallery-v2-close-button-text-background-color-hover;
    }

    slot[name='horizontal-accordion-close-icon'],
    ::slotted(*) {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      fill: currentColor;
      color: config.$accordion-gallery-v2-close-button-text-color;
      font-size: 1.4rem; //CMP-4147 //~based on icon-font-size: 1.1rem; see above
    }
  }

  content-box-toggle {
    --button-flat-raised-y-padding: 3px;
    --button-flat-raised-x-padding: 3px;

    @extend %csc-button-float;

    display: flex;

    position: absolute;
    z-index: 15;
    bottom: 0.75rem;
    left: 50%;
    box-sizing: border-box;

    align-content: center;
    justify-content: center;
    max-width: 2rem;
    margin: auto;

    transform: translateX(-50%);
    transition:
      opacity config.$animation-duration config.$animation-cubic,
      background-color config.$animation-duration config.$animation-cubic;
    background-color: config.$accordion-gallery-v2-content-box-toggle-background-color-hidden;

    @include breakpoints.breakpoint('lg') {
      /* Do not show the content-box toggle on desktop */
      display: none;
    }

    slot[name='vertical-accordion-close-icon'],
    slot[name='vertical-accordion-info-icon'] {
      display: none;
      align-content: center;
      justify-content: center;
      width: 2rem;
      padding: 3px;
      border: 1.5px solid config.$accordion-gallery-v2-content-box-toggle-background-color-visible;
      border-radius: 100%;
    }

    slot[name='vertical-accordion-info-icon'] {
      display: flex;
    }

    slot {
      transition: color config.$animation-duration config.$animation-cubic;
      fill: currentColor;
      color: config.$accordion-gallery-v2-content-box-toggle-text-color-hidden;
    }
  }

  /* Tab title for closed (shrinked) tab */
  tab-headline {
    display: block;
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: opacity config.$animation-duration-short config.$animation-cubic;
    opacity: 0;
    background-color: config.$accordion-gallery-v2-content-title-background-color;
    pointer-events: none;

    @include breakpoints.breakpoint('lg', 'down') {
      height: 2.5rem;
      opacity: 1;
      background-color: config.$accordion-gallery-v2-content-title-background-color-mobile;
    }

    /* Centered and rotated label */
    &::before {
      content: attr(data-title);
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translateX(-50%) translateY(-50%) rotateZ(90deg);
      color: config.$accordion-gallery-v2-content-title-text-color;
      font-weight: config.$accordion-gallery-v2-content-title-font-weight;
      white-space: nowrap;

      /* Cause for flex-direction column, do not rotate on mobile */
      @include breakpoints.breakpoint('lg', 'down') {
        transform: translateX(-50%) translateY(-50%);
      }
    }
  }

  /* Content box for product information or something */
  tab-content-box {
    display: flex;
    position: absolute;
    z-index: 1;
    bottom: 2rem;
    left: 50%;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    width: 75%;
    min-width: 10rem;
    max-width: 21rem;
    padding: 1.5rem 1rem 1rem;
    overflow: hidden;
    transform: translateX(-50%);
    transition:
      max-height config.$animation-duration config.$animation-cubic,
      max-width config.$animation-duration config.$animation-cubic,
      opacity config.$animation-duration config.$animation-cubic,
      left config.$animation-duration config.$animation-cubic,
      transform config.$animation-duration config.$animation-cubic;
    background: config.$accordion-gallery-v2-content-box-background-color;
    text-align: left;

    @include breakpoints.breakpoint('lg', 'down') {
      min-width: unset;
      max-width: 50%;
      height: auto !important;
      overflow: auto;
    }

    @include breakpoints.breakpoint('md', 'down') {
      max-width: calc(100% - 2rem);
      padding: 1rem;
    }
  }

  /* Caption */
  slot[name='content-headline'] {
    @include breakpoints.breakpoint('xs', 'only') {
      margin-bottom: 4px;
    }

    /* Mobile */
    @include breakpoints.breakpoint('lg', 'down') {
      color: config.$accordion-gallery-v2-content-box-caption-text-color;
    }
  }

  /* Details will be shown on expanded content */
  tab-details {
    max-width: 100%;
    max-height: 0.00001vh; /* Unit is important for IE */
    overflow: hidden;
    transition: max-height config.$animation-duration config.$animation-cubic;
    text-align: left;
  }

  slot[name='content-cta'] {
    display: block;
    align-self: center;
    max-height: 5rem;
    overflow: hidden;
    transition: 0s config.$animation-duration;
    text-transform: config.$accordion-gallery-v2-content-button-text-transform;
    pointer-events: auto;
  }
}

:host([info-box-opened]) {
  tab-content-box {
    opacity: 1;
  }

  content-box-toggle {
    slot[name='vertical-accordion-close-icon'] {
      display: flex;
    }

    slot[name='vertical-accordion-info-icon'] {
      display: none;
    }
  }
}

:host(:not([info-box-opened])) {
  tab-content-box {
    opacity: 0;
  }
}

:host([state='opened']) {
  outline-width: 1px;
  /* for js */

  /* Mobile */
  @include breakpoints.breakpoint('lg', 'down') {
    height: 100%;
  }

  /* Desktop */
  @include breakpoints.breakpoint('lg', 'up') {
    width: 100%;
  }

  /* Disable label which opens the tab. Just to make sure it is not over the content */
  click-target {
    display: none;
  }

  /* Show close button */
  close {
    opacity: 1;
    pointer-events: auto;
  }

  /* Show content box with details on expanded content */
  tab-content-box {
    z-index: 3;

    @include breakpoints.breakpoint('lg', 'up') {
      left: 2rem;
      transform: none;
      opacity: 1;
    }
  }

  tab-details {
    max-height: calc(#{config.$accordion-gallery-v2-height} * 0.8);
    /* Needed for animation */

    @include breakpoints.breakpoint(sm, up) {
      max-height: calc(#{config.$accordion-gallery-v2-large-height} * 0.8);
    }
  }

  /* Hide button on expanded content */
  slot[name='content-cta'] {
    display: block;
    min-height: 0;
    max-height: 0;
    padding: 0;
    transition-delay: 0s;
  }
}

:host([state='closed']) {
  /* Mobile */
  @include breakpoints.breakpoint('lg', 'down') {
    height: 0;

    content-box-toggle {
      display: flex;
      opacity: 0;
      pointer-events: none;
    }
  }

  /* Desktop */
  @include breakpoints.breakpoint('lg', 'up') {
    width: 0;
  }

  tab-headline {
    transition-duration: math.div(config.$animation-duration, 2);
    transition-delay: math.div(config.$animation-duration, 2);
    opacity: 1;
  }

  tab-content-box {
    opacity: 0;
    pointer-events: none;
  }
}

:host([allclosed]) {
  @include breakpoints.breakpoint('lg', 'up') {
    slot[name='background'] {
      opacity: 1;
    }

    tab-headline {
      opacity: 0;
    }

    tab-content-box {
      align-items: center;
      opacity: 1;
      text-align: center;
    }
  }
}
